import React, { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Stack,
  Box,
  Typography,
  IconButton,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Autocomplete,
  Chip
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import datapointsData from '../../data/datapoints.json';
import { getAssetTypeLabel, getAssetLabel, truncateGuid } from '../utils/assetUtils';

const TIMEFRAMES = ['TaskStart', 'Lifetime', 'Daily', 'Weekly', 'QuestLineStart'];
const COMMON_DATAPOINTS = datapointsData;

export default function TaskEditDialog({ 
  open, 
  onClose, 
  task, 
  assets = [],
  onSave 
}) {
  const [formData, setFormData] = useState({
    title: '',
    dataPoint: '',
    amount: 1,
    timeframe: 'TaskStart',
    targetIds: '',
    scene: '',
    requestedItems: [],
    taskOrder: 0,
    isActive: 1,
    conversationIds: {
      startConversationId: null,
      endConversationId: null,
      failedConversationId: null
    }
  });

  const [inputValues, setInputValues] = useState({});

  useEffect(() => {
    if (task) {
      setFormData({
        title: task.title || '',
        dataPoint: task.dataPoint || '',
        amount: task.amount || 1,
        timeframe: task.timeframe || 'TaskStart',
        targetIds: task.targetIds || '',
        scene: task.scene || '',
        requestedItems: parseJSON(task.requestedItems),
        taskOrder: task.taskOrder || 0,
        isActive: task.isActive ?? 1,
        conversationIds: task.conversationIds || {
          startConversationId: null,
          endConversationId: null,
          failedConversationId: null
        }
      });
    } else {
      // Reset for new task
      setFormData({
        title: '',
        dataPoint: '',
        amount: 1,
        timeframe: 'TaskStart',
        targetIds: '',
        scene: '',
        requestedItems: [],
        taskOrder: 0,
        isActive: 1,
        conversationIds: {
          startConversationId: null,
          endConversationId: null,
          failedConversationId: null
        }
      });
    }
    setInputValues({});
  }, [task, open]);

  const parseJSON = (field) => {
    try {
      if (typeof field === 'string') return JSON.parse(field);
      return field || [];
    } catch {
      return [];
    }
  };

  const filterOptions = useMemo(() => (options, state) => {
    const inputValue = state.inputValue;
    if (!inputValue || !inputValue.trim()) {
      return [];
    }
    const searchTerm = inputValue.trim().toLowerCase();
    const filtered = options.filter(option => {
      const title = (option.title || '').toLowerCase();
      const id = (option.id || '').toLowerCase();
      return title.includes(searchTerm) || id.includes(searchTerm);
    });
    return filtered;
  }, []);

  const handleAddRequestedItem = () => {
    setFormData(prev => ({
      ...prev,
      requestedItems: [...prev.requestedItems, { targetId: '', amount: 1 }]
    }));
  };

  const handleRemoveRequestedItem = (index) => {
    setFormData(prev => ({
      ...prev,
      requestedItems: prev.requestedItems.filter((_, i) => i !== index)
    }));
    const key = `requested-${index}`;
    setInputValues(prev => {
      const newValues = { ...prev };
      delete newValues[key];
      return newValues;
    });
  };

  const handleRequestedItemChange = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      requestedItems: prev.requestedItems.map((item, i) => 
        i === index ? { ...item, [field]: value } : item
      )
    }));
  };

  const handleSave = () => {
    onSave(formData);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{task ? 'Edit Task' : 'Create Task'}</DialogTitle>
      <DialogContent>
        <Stack spacing={3} sx={{ mt: 1 }}>
          <TextField
            label="Title"
            fullWidth
            value={formData.title}
            onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
          />

          <Autocomplete
            freeSolo
            options={COMMON_DATAPOINTS}
            value={formData.dataPoint}
            onChange={(e, newValue) => setFormData(prev => ({ ...prev, dataPoint: newValue || '' }))}
            renderInput={(params) => <TextField {...params} label="DataPoint" />}
          />

          <TextField
            label="Amount"
            type="number"
            fullWidth
            value={formData.amount}
            onChange={(e) => setFormData(prev => ({ ...prev, amount: parseInt(e.target.value) || 1 }))}
          />

          <FormControl fullWidth>
            <InputLabel>Timeframe</InputLabel>
            <Select
              value={formData.timeframe}
              label="Timeframe"
              onChange={(e) => setFormData(prev => ({ ...prev, timeframe: e.target.value }))}
            >
              {TIMEFRAMES.map(tf => (
                <MenuItem key={tf} value={tf}>{tf}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <Box>
            <Typography variant="subtitle2" mb={1}>Target IDs</Typography>
            <Stack spacing={1}>
              <Autocomplete
                multiple
                freeSolo
                options={assets}
                getOptionLabel={(option) => {
                  if (typeof option === 'string') return option;
                  const typeLabel = getAssetTypeLabel(option);
                  return `${option.title} (${typeLabel})`;
                }}
                filterOptions={(options, { inputValue }) => {
                  if (!inputValue) return options;
                  const searchTerm = inputValue.toLowerCase();
                  return options.filter(option => 
                    option.title?.toLowerCase().includes(searchTerm) ||
                    option.id?.toLowerCase().includes(searchTerm) ||
                    option.type?.toLowerCase().includes(searchTerm) ||
                    option.assetType?.toLowerCase().includes(searchTerm)
                  );
                }}
                value={formData.targetIds ? formData.targetIds.split(',').map(id => id.trim()).filter(Boolean) : []}
                onChange={(e, newValue) => {
                  const ids = newValue.map(v => typeof v === 'string' ? v : v.id);
                  setFormData(prev => ({ ...prev, targetIds: ids.join(',') }));
                }}
                renderInput={(params) => (
                  <TextField 
                    {...params} 
                    placeholder="Search assets or paste IDs"
                    size="small"
                  />
                )}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => {
                    const asset = assets.find(a => a.id === option);
                    if (asset) {
                      const typeLabel = getAssetTypeLabel(asset);
                      return (
                        <Chip
                          key={index}
                          {...getTagProps({ index })}
                          label={`${asset.title} (${typeLabel})`}
                          size="small"
                        />
                      );
                    }
                    return (
                      <Chip
                        key={index}
                        {...getTagProps({ index })}
                        label={`${option.substring(0, 12)}...`}
                        size="small"
                      />
                    );
                  })
                }
              />
              <TextField
                fullWidth
                size="small"
                value={formData.targetIds}
                onChange={(e) => setFormData(prev => ({ ...prev, targetIds: e.target.value }))}
                placeholder="Or paste comma-separated IDs directly"
                helperText="You can search assets above or paste IDs here (comma-separated)"
              />
            </Stack>
          </Box>

          <TextField
            label="Scene"
            fullWidth
            value={formData.scene}
            onChange={(e) => setFormData(prev => ({ ...prev, scene: e.target.value }))}
            placeholder="e.g., Makubo, PortOhana (optional)"
            helperText="Filter DataPoints by scene name. Leave empty to count from all scenes."
          />

          <TextField
            label="Task Order"
            type="number"
            fullWidth
            value={formData.taskOrder}
            onChange={(e) => setFormData(prev => ({ ...prev, taskOrder: parseInt(e.target.value) || 0 }))}
          />

          {/* Requested Items */}
          <Box>
            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
              <Typography variant="subtitle2">Required Items</Typography>
              <Button size="small" startIcon={<AddIcon />} onClick={handleAddRequestedItem}>
                Add Item
              </Button>
            </Stack>
            <Stack spacing={1}>
              {formData.requestedItems.map((item, index) => {
                const key = `requested-${index}`;
                return (
                  <Stack key={index} direction="row" spacing={1} alignItems="center">
                    <Autocomplete
                      options={assets}
                      getOptionLabel={(option) => getAssetLabel(option)}
                      isOptionEqualToValue={(option, value) => option.id === value?.id}
                      filterOptions={filterOptions}
                      openOnFocus={false}
                      inputValue={inputValues[key] || ''}
                      onInputChange={(e, newInputValue) => {
                        setInputValues(prev => ({ ...prev, [key]: newInputValue }));
                      }}
                      value={assets.find(a => a.id === item.targetId) || null}
                      onChange={(e, newValue) => {
                        handleRequestedItemChange(index, 'targetId', newValue?.id || '');
                        if (newValue) {
                          setInputValues(prev => ({ ...prev, [key]: '' }));
                        }
                      }}
                      renderOption={(props, option) => (
                        <Box component="li" {...props}>
                          <Box>
                            <Typography variant="body2">
                              {getAssetLabel(option)}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                              {truncateGuid(option.id)}
                            </Typography>
                          </Box>
                        </Box>
                      )}
                      renderInput={(params) => <TextField {...params} label="Asset" size="small" />}
                      sx={{ flexGrow: 1 }}
                    />
                    <TextField
                      label="Amount"
                      type="number"
                      size="small"
                      value={item.amount}
                      onChange={(e) => handleRequestedItemChange(index, 'amount', parseInt(e.target.value) || 1)}
                      sx={{ width: '120px' }}
                    />
                    <IconButton size="small" color="error" onClick={() => handleRemoveRequestedItem(index)}>
                      <DeleteIcon />
                    </IconButton>
                  </Stack>
                );
              })}
            </Stack>
          </Box>

          {/* Conversation IDs */}
          <Box>
            <Typography variant="subtitle2" mb={1}>Conversation IDs</Typography>
            <Stack spacing={2}>
              <TextField
                label="Start Conversation ID"
                fullWidth
                value={formData.conversationIds?.startConversationId || ''}
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  conversationIds: {
                    ...prev.conversationIds,
                    startConversationId: e.target.value || null
                  }
                }))}
                placeholder="GUID for conversation shown when task starts"
              />
              <TextField
                label="End Conversation ID"
                fullWidth
                value={formData.conversationIds?.endConversationId || ''}
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  conversationIds: {
                    ...prev.conversationIds,
                    endConversationId: e.target.value || null
                  }
                }))}
                placeholder="GUID for conversation shown when task ends"
              />
              <TextField
                label="Failed Conversation ID"
                fullWidth
                value={formData.conversationIds?.failedConversationId || ''}
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  conversationIds: {
                    ...prev.conversationIds,
                    failedConversationId: e.target.value || null
                  }
                }))}
                placeholder="GUID for conversation shown when task fails"
              />
            </Stack>
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleSave} variant="contained">Save</Button>
      </DialogActions>
    </Dialog>
  );
}
